<template>
  <div class="conv-card">
    <div class="top-row">
      <div class="title-row">
        <span class="avatar">{{ initials }}</span>

        <div>
          <div class="visitor-name">
            {{ c.visitor_name || "Visitor" }}
          </div>

          <div class="sub-info">
            #{{ c.conversation_id }}
            <span v-if="c.visitor_phone"> • {{ c.visitor_phone }}</span>
          </div>
        </div>
      </div>

      <div class="assigned" :class="{ unassigned: isUnassigned }">
        {{ c.assigned_username ? "@" + c.assigned_username : "Unassigned" }}
      </div>
    </div>

    <div class="badge-row">
      <span class="badge" :class="c.status">{{ c.status }}</span>

      <span v-if="c.dispo_status" class="badge dispo">
        {{ c.dispo_status }}
      </span>

      <span v-if="c.channel" class="badge channel">
        {{ c.channel }}
      </span>

      <span v-if="c.tenant_name" class="badge tenant">
        {{ c.tenant_name }}
      </span>
    </div>

    <div v-if="c.last_message" class="message-box">
      {{ c.last_message }}
    </div>

    <div v-if="c.form_json" class="meta-box">
      {{ c.form_json }}
    </div>

    <div class="footer">
      <div class="action-row">
        <button class="btn primary" @click="$emit('view', c)">
          View
        </button>
        <!-- {{ isUnassigned }} -->
        <button
          v-if="isUnassigned && c.status !== 'CLOSED'"
          class="btn chat-now"
          @click="chatNow"
        >
          Chat Now
        </button>

        <button
          v-if="c.status !== 'CLOSED'"
          class="btn danger"
          @click="$emit('close', c.conversation_id)"
        >
          Close
        </button>
      </div>

      <div v-if="c.status !== 'CLOSED'" class="assign-row">
        <select v-model="localAssign">
          <option value="">Assign Agent</option>

          <option
            v-for="a in agents"
            :key="a.id"
            :value="a.id"
          >
            {{ a.username }} ({{ a.state }})
          </option>
        </select>

        <button
          class="btn assign"
          @click="assign"
          :disabled="!localAssign"
        >
          Assign
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

const props = defineProps({
  c: {
    type: Object,
    required: true
  },
  agents: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(["view", "assign", "close", "chat-now"]);

const localAssign = ref("");

const isUnassigned = computed(() => {
  return !props.c.assigned_to && !props.c.assigned_username;
});

const initials = computed(() => {
  const name = props.c.visitor_name || "Visitor";

  return name
    .split(" ")
    .filter(Boolean)
    .map((x) => x[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
});

watch(
  () => props.c.conversation_id,
  () => {
    localAssign.value = "";
  }
);

function assign() {
  if (!localAssign.value) return;

  emit("assign", {
    conversation_id: props.c.conversation_id,
    agent_id: localAssign.value
  });
}

function chatNow() {
  emit("chat-now", {
    conversation_id: props.c.conversation_id,
    conversation: props.c
  });
}
</script>

<style scoped>
.conv-card {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e6ebf1;
  border-radius: 16px;
  padding: 15px;
  margin-bottom: 12px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
  transition: 0.2s ease;
}

.conv-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.1);
}

.top-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.avatar {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: #eef4ff;
  color: #2563eb;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
}

.visitor-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.sub-info {
  margin-top: 2px;
  font-size: 11px;
  color: #64748b;
}

.assigned {
  font-size: 11px;
  background: #f1f5f9;
  color: #334155;
  padding: 5px 9px;
  border-radius: 999px;
  white-space: nowrap;
}

.assigned.unassigned {
  background: #fff7ed;
  color: #c2410c;
}

.badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.badge {
  font-size: 10px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.badge.OPEN {
  background: #e0f2fe;
  color: #0369a1;
}

.badge.CLOSED {
  background: #fee2e2;
  color: #b91c1c;
}

.badge.dispo {
  background: #fef3c7;
  color: #92400e;
}

.badge.channel {
  background: #dcfce7;
  color: #166534;
}

.badge.tenant {
  background: #ede9fe;
  color: #5b21b6;
}

.message-box {
  margin-top: 12px;
  background: #ffffff;
  border: 1px solid #edf2f7;
  border-radius: 12px;
  padding: 10px;
  font-size: 13px;
  color: #334155;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.meta-box {
  margin-top: 8px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  padding: 8px;
  font-size: 11px;
  color: #64748b;
  word-break: break-word;
}

.footer {
  margin-top: 13px;
}

.action-row,
.assign-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.assign-row {
  margin-top: 8px;
}

.assign-row select {
  flex: 1;
  min-width: 0;
}

.btn,
select {
  height: 30px;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 12px;
}

select {
  border: 1px solid #dbe3ec;
  background: #ffffff;
  color: #334155;
  outline: none;
}

.btn {
  min-width: 70px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  white-space: nowrap;
  transition: 0.15s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.btn.primary {
  background: #2563eb;
  color: #ffffff;
}

.btn.chat-now {
  background: #16a34a;
  color: #ffffff;
}

.btn.assign {
  background: #0f172a;
  color: #ffffff;
}

.btn.danger {
  background: #fee2e2;
  color: #dc2626;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .top-row {
    flex-direction: column;
  }

  .assigned {
    align-self: flex-start;
  }

  .action-row,
  .assign-row {
    width: 100%;
  }

  .btn {
    flex: 1;
    min-width: 0;
  }
}
</style>